import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Button, message, Popconfirm } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { EmptyState } from '../empty_state/EmptyState';
import UserGroupRowDisplay from './UserGroupRowDisplay';
import UserGroupMemberCounts from './UserGroupMemberCounts';
import UserGroupFormModal from './UserGroupFormModal';
import UserGroupDetailModal from './UserGroupDetailModal';
import { USER_GROUPS_CRUD_ACTION_CREATORS } from '../../actions/user_groups_actions';
import { userGroupsRestCrudThunks } from '../../thunks/user_groups_thunks';
import { withUserType } from '../withUserType';
import { nullSafeGet, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { DEFAULT_NAME_SORTER } from '../../utils/DataConstants';
import { USER_GROUPS_INDEX_TARGET_COLLECTION as UG_TC_NAME } from '../../types/user_groups';
import { isAdminUserGroupsEnabled } from '../../utils/FeatureFlagUtils';

const UserGroupsIndexPage: FC<any> = ({
	userGroups,
	updateFilters,
	clearAndUpdateFilters,
	fetchUserGroups,
	fetchUserGroup,
	deleteUserGroup,
	userType,
}): React.ReactElement => {
	const [formModalVisible, setFormModalVisible] = useState(false);
	const [currentEditingRecord, setCurrentEditingRecord] = useState(null);
	// Id of the row whose full record is being fetched before its edit modal opens.
	const [hydratingId, setHydratingId] = useState(null);
	// Group whose detail modal is open (set by clicking the row).
	const [detailRecord, setDetailRecord] = useState(null);
	// Id of the row whose delete is in flight.
	const [deletingId, setDeletingId] = useState(null);

	const onAddUserGroupClick = useCallback(() => {
		setCurrentEditingRecord(null);
		setFormModalVisible(true);
	}, []);

	const hideFormModal = useCallback(() => {
		setFormModalVisible(false);
		setCurrentEditingRecord(null);
	}, []);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${UG_TC_NAME}.${propName}`, userGroups, {}),
		[userGroups]
	);

	const refreshUserGroups = useCallback(() => {
		fetchUserGroups(
			{},
			UG_TC_NAME,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchUserGroups, getStateSliceProp]);

	// A write returns the freshly hydrated group, so push it back into the detail modal (which
	// is prop-driven) rather than leaving it showing pre-edit data underneath the form.
	const applySavedToDetail = useCallback((saved) => {
		if (!saved) {
			return;
		}
		setDetailRecord((current) =>
			current && nullSafeGet('id', current) === nullSafeGet('id', saved) ? saved : current
		);
	}, []);

	const onCreateOrUpdateSuccess = useCallback(
		(saved) => {
			hideFormModal();
			applySavedToDetail(saved);
			refreshUserGroups();
		},
		[applySavedToDetail, hideFormModal, refreshUserGroups]
	);

	// Members removed inside the detail modal: same treatment, plus a list refresh so the
	// index row's counts follow.
	const onDetailMembersChanged = useCallback(
		(saved) => {
			applySavedToDetail(saved);
			refreshUserGroups();
		},
		[applySavedToDetail, refreshUserGroups]
	);

	// Always open the edit modal off a fresh single-GET rather than a list row or a captured
	// prop. Beyond guarding against a list response that omits `memberEmails`, this keeps the
	// detail modal's Edit honest: members removed inside that modal would otherwise still be
	// present in the record it was rendered with. Seed from the value the promise resolves
	// with, never the slice's `detail` slot, which is a global "last touched entity" pointer.
	const openEditModal = useCallback(
		(record) => {
			const id = nullSafeGet('id', record);
			setHydratingId(id);
			return fetchUserGroup(id)
				.then((hydrated) => {
					setCurrentEditingRecord(hydrated || record);
					setFormModalVisible(true);
				})
				.catch(() => message.error('Failed to load this user group.'))
				.finally(() => setHydratingId(null));
		},
		[fetchUserGroup]
	);

	const onEdit = useCallback(
		(record) => (e) => {
			stopDefaultEvents(e);
			openEditModal(record);
		},
		[openEditModal]
	);

	const onRowClick = useCallback(
		(record) => ({
			onClick: () => setDetailRecord(record),
		}),
		[]
	);

	const closeDetailModal = useCallback(() => setDetailRecord(null), []);

	// Edit from inside the detail modal: leave the detail view mounted and open the form over
	// it. Both modals portal to the body, so the later-mounted form paints on top.
	const onEditFromDetail = useCallback(() => {
		openEditModal(detailRecord);
	}, [detailRecord, openEditModal]);

	// The thunk is built with showMessages=false, so a failure is silent unless we surface it
	// here — and an uncaught rejection would otherwise escape the promise.
	const onDelete = useCallback(
		(record) => () => {
			// `deleteImmediate` fires the DELETE straight away, so this spinner covers the real
			// request plus the list refresh that follows it.
			setDeletingId(nullSafeGet('id', record));
			deleteUserGroup(record)
				.then(() => refreshUserGroups())
				.catch(() => message.error('Failed to delete this user group.'))
				.finally(() => setDeletingId(null));
		},
		[deleteUserGroup, refreshUserGroups]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				// Name and Users split the row 50/50, so the Users column starts at the midpoint.
				// The action column's fixed width comes out of that split, so the two halves are
				// equal to each other rather than exactly half the full table width.
				width: '50%',
				render: (_, record) => <UserGroupRowDisplay record={record} />,
			},
			{
				title: 'Users',
				dataIndex: 'activeUsersCount',
				// Both columns are left-aligned (antd's default), so no `align` override and no
				// centring block: the counts line up down the column on their own.
				width: '50%',
				render: (_, record) => <UserGroupMemberCounts record={record} />,
			},
			{
				title: '',
				dataIndex: '',
				width: 112,
				render: (_, record) => (
					// The row itself opens the detail modal, so the action cell swallows its own
					// clicks — including the Popconfirm's, which is injected onto its child and so
					// cannot be intercepted on the button itself.
					<div className="flex items-center justify-end gap-4" onClick={stopDefaultEvents}>
						{/* Icon-only buttons: `title` gives the hover hint and `aria-label` the
						    accessible name. Deliberately not wrapped in a Tooltip — the delete
						    button is a Popconfirm child, and Popconfirm injects its own onClick
						    onto that child, so an intervening Tooltip (or an onClick of our own)
						    would swallow the click that opens the confirm. */}
						<Button
							type="ghost"
							icon={<EditOutlined />}
							onClick={onEdit(record)}
							loading={hydratingId === nullSafeGet('id', record)}
							title="Edit"
							aria-label="Edit user group"
						/>
						{/* Deleting a group also drops it from anything it was assigned to,
						    so it always goes through a confirm. */}
						<Popconfirm
							title="Delete this user group?"
							onConfirm={onDelete(record)}
							okText="Delete"
							okButtonProps={{ danger: true }}
							cancelText="Cancel"
						>
							<Button
								type="ghost"
								danger
								icon={<DeleteOutlined />}
								loading={deletingId === nullSafeGet('id', record)}
								title="Delete"
								aria-label="Delete user group"
							/>
						</Popconfirm>
					</div>
				),
			},
		],
		[deletingId, hydratingId, onDelete, onEdit]
	);

	// The tab is hidden while the flag is off; this catches a direct link or bookmark to the route.
	if (!isAdminUserGroupsEnabled()) {
		return <Redirect to={`/${userType}/admin/users`} />;
	}

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={UG_TC_NAME}
				updateFilters={updateFilters}
				stateSlice={userGroups}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={[]}
				entityCollectionName="user_groups"
				// Without this the wrapper passes `showHeader={!!undefined}` and the whole column
				// header row is hidden, so the Name / Users titles never render.
				showHeader
				tableColumns={columns}
				fetchData={userGroupsRestCrudThunks(userType).read}
				initialSorters={DEFAULT_NAME_SORTER}
				onTableRow={onRowClick}
				rightActions={
					<div>
						<Button
							style={{ marginLeft: 16 }}
							type="primary"
							size="large"
							onClick={onAddUserGroupClick}
						>
							Add User Group
						</Button>
					</div>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="No Data"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
							/>
						}
						headline={'No user groups yet'}
						body={
							<div className="text-center">
								<div className="mb-4">
									Group people together so you can add them to announcements, work orders and
									walk-throughs all at once.
								</div>
								<Button type="primary" onClick={onAddUserGroupClick}>
									Add User Group
								</Button>
							</div>
						}
					/>
				}
			/>
			{detailRecord && (
				<UserGroupDetailModal
					userGroup={detailRecord}
					onCancel={closeDetailModal}
					onEditClick={onEditFromDetail}
					editLoading={hydratingId === nullSafeGet('id', detailRecord)}
					onChanged={onDetailMembersChanged}
				/>
			)}
			{formModalVisible && (
				<UserGroupFormModal
					userGroup={currentEditingRecord}
					onSuccess={onCreateOrUpdateSuccess}
					onCancel={hideFormModal}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	userGroups: state.user_groups,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchUserGroups: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			userGroupsRestCrudThunks(ownProps.userType).read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	// Single-GET used to hydrate a group's full membership before opening the edit modal.
	fetchUserGroup: (id) => dispatch(userGroupsRestCrudThunks(ownProps.userType).readOne(id)),
	// `deleteImmediate`, not `delete`: the generator's default `delete` defers the request by
	// 4 seconds for an undo window, and with showUndo=false it shows no undo affordance either
	// — the row would just sit there for 4s before the refresh landed. The Popconfirm above is
	// already the confirmation step, so fire the DELETE straight away.
	deleteUserGroup: (entity) =>
		dispatch(userGroupsRestCrudThunks(ownProps.userType).deleteImmediate(entity)),
	updateFilters: (filters, targetCollection) =>
		dispatch(USER_GROUPS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(USER_GROUPS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(UserGroupsIndexPage));
